/**
 * The quotes API, for the actions registry (S2; docs/audits/PILOT-QUOTES-SHELL.md §3.2).
 *
 * Thin wrappers over authFetch (which sets the Authorization header: no manual headers
 * here). A non-2xx answer throws QuoteApiError carrying the server's own reason, so the
 * runner can show it instead of a generic "Failed to …". The server answers in two shapes:
 *   { success: false, error: { code, message } }   ApiError (routes/quotes.js, PR-2l)
 *   { error: 'message' }                           the older approvals routes
 */
import { authFetch } from './authFetch';

const API_URL = process.env.REACT_APP_API_URL || '';

export class QuoteApiError extends Error {
  constructor(status, message, body = null) {
    super(message);
    this.name = 'QuoteApiError';
    this.status = status;
    this.body = body;
  }
}

/** The server's reason in a response body, whichever shape it used. */
export function serverMessage(body) {
  if (!body || typeof body !== 'object') return null;
  if (body.error && typeof body.error === 'object' && body.error.message) return body.error.message;
  if (typeof body.error === 'string' && body.error) return body.error;
  if (typeof body.message === 'string' && body.message) return body.message;
  return null;
}

/**
 * One request. Resolves to the parsed body (or null when there is none); rejects with a
 * QuoteApiError whose message is the server's reason, or "Request failed (<status>)".
 */
export async function requestJson(method, path, body) {
  const res = await authFetch(`${API_URL}${path}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    throw new QuoteApiError(res.status, serverMessage(data) || `Request failed (${res.status})`, data);
  }
  return data;
}

/** The record itself, unwrapped from { data } when the route wraps it. */
const unwrap = (data) => (data && data.data !== undefined ? data.data : data);

export const quotesApi = {
  getQuote: (id) => requestJson('GET', `/api/quotes/${id}`),
  updateStatus: (id, status, { lostReason } = {}) =>
    requestJson('PATCH', `/api/quotes/${id}/status`, { status, ...(lostReason ? { lostReason } : {}) }).then(unwrap),
  deleteQuote: (id) => requestJson('DELETE', `/api/quotes/${id}`),
  requestApproval: (id, payload) => requestJson('POST', `/api/quotations/${id}/request-approval`, payload),
  approve: (approvalId, comments = '') => requestJson('POST', `/api/approvals/${approvalId}/approve`, { comments }),
  reject: (approvalId, comments) => requestJson('POST', `/api/approvals/${approvalId}/reject`, { comments }),
  bulkStatus: (ids, status) => requestJson('POST', '/api/quotations/bulk/status', { quoteIds: ids, status }),
  bulkExtendExpiry: (ids, days) => requestJson('POST', '/api/quotations/bulk/extend-expiry', { quoteIds: ids, days }),
  bulkAssign: (ids, salesRepId, salesRepName) =>
    requestJson('POST', '/api/quotations/bulk/assign', { quoteIds: ids, salesRepId, salesRepName }),
  bulkDelete: (ids) => requestJson('DELETE', '/api/quotations/bulk', { quoteIds: ids }),
  bulkEmail: (payload) => requestJson('POST', '/api/quotations/bulk/email', payload)
};

export default quotesApi;
